



import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useStore } from '../../context/StoreContext';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Shield, Camera, LogOut, Sparkles, FileText, Zap, Settings, ExternalLink, Heart } from 'lucide-react';

const UserProfilePage: React.FC = () => {
  const { user, signOut } = useAuth();
  const { business } = useStore();
  const navigate = useNavigate();
  
  const fullName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Empreendedor';
  const avatarUrl = user?.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=random`;

  return (
    <div className="max-w-4xl mx-auto space-y-10 animate-fade-in pb-20">
      <header className="flex flex-col md:flex-row justify-between items-start gap-6">
        <div>
            <h1 className="text-5xl font-black text-white tracking-tighter uppercase leading-none italic">COMMAND <span className="text-rose-500">CENTER</span></h1>
            <p className="text-gray-500 font-bold uppercase text-[10px] tracking-widest mt-4 flex items-center gap-2">
            <Shield className="w-4 h-4 text-rose-500" /> Credenciais e Gestão de Engine
            </p>
        </div>
        <button 
            // FIX: Property 'slug' does not exist on type 'InstagramProfile'. Using username instead.
            onClick={() => window.open(`/${business?.username}`, '_blank')}
            className="bg-white/5 border border-white/10 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-white hover:text-black transition-all"
        >
            Ver Cardápio <ExternalLink className="w-4 h-4" />
        </button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-[3rem] p-10 text-center relative overflow-hidden group shadow-2xl">
                <div className="relative mb-6 inline-block">
                    <div className="w-32 h-32 rounded-[2.5rem] p-1 bg-gradient-to-tr from-rose-500 to-orange-500 shadow-2xl group-hover:rotate-3 transition-transform">
                        <img src={avatarUrl} className="w-full h-full object-cover rounded-[2.3rem] border-4 border-secondary-950" alt="Avatar" />
                    </div>
                </div>

                <h3 className="text-2xl font-black text-white mb-1 uppercase tracking-tighter truncate px-2">{fullName}</h3>
                <p className="text-rose-500 font-bold text-[10px] uppercase tracking-widest mb-8">Proprietário Master</p>
                
                <div className="space-y-3">
                    <button onClick={signOut} className="w-full py-4 bg-red-500/10 text-red-500 font-black rounded-2xl text-[10px] uppercase tracking-widest border border-red-500/20 hover:bg-red-500 hover:text-white transition-all">
                        Desconectar
                    </button>
                </div>
            </div>

            <div className="bg-gradient-to-tr from-rose-600 to-orange-500 p-8 rounded-[2.5rem] shadow-2xl space-y-4">
                <div className="flex justify-between items-start">
                  <h4 className="text-white font-black text-xs uppercase tracking-widest">Nível de Conta</h4>
                  <Heart className="w-4 h-4 text-white fill-current" />
                </div>
                <div className="flex justify-between items-end">
                    <span className="text-white font-black text-2xl italic">VITALÍCIO</span>
                    <span className="text-white/80 text-[10px] font-bold uppercase">Ilimitado</span>
                </div>
            </div>
        </div>

        <div className="lg:col-span-2 space-y-8">
            <div className="bg-white/5 border border-white/10 rounded-[3.5rem] p-10 shadow-2xl space-y-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">E-mail Administrativo</label>
                        <div className="bg-black/40 border border-white/5 rounded-2xl p-4 flex items-center gap-3">
                            <Mail className="w-4 h-4 text-rose-500" />
                            <span className="text-white font-medium text-sm truncate">{user?.email}</span>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Status da Engine</label>
                        <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-4 flex items-center gap-3">
                            <Zap className="w-4 h-4 text-green-400" />
                            <span className="text-green-400 font-black text-[10px] uppercase tracking-widest">Operacional</span>
                        </div>
                    </div>
                </div>

                <div className="pt-6 border-t border-white/5 space-y-8">
                    <h4 className="text-white font-black flex items-center gap-2">
                        <Settings className="w-4 h-4 text-rose-500" /> Configurações Rápidas
                    </h4>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <QuickAction 
                            icon={Settings} 
                            label="Reajustar Setup" 
                            onClick={() => {
                                navigate('/admin/setup');
                            }} 
                        />
                        <QuickAction 
                            icon={Sparkles} 
                            label="Otimizar Menu" 
                            onClick={() => navigate('/admin/menu')} 
                        />
                    </div>

                    <div className="bg-rose-500/5 border border-rose-500/10 p-6 rounded-[2rem] text-gray-400 italic font-medium leading-relaxed text-sm">
                        <Sparkles className="w-4 h-4 text-rose-500 mb-2" />
                        "Sua conta está configurada com acesso total a todas as ferramentas de IA e CRM do Fluxil. Boas vendas!"
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

const QuickAction = ({ icon: Icon, label, onClick }: any) => (
    <button 
        onClick={onClick}
        className="flex items-center gap-4 bg-white/5 border border-white/10 p-5 rounded-2xl hover:bg-white hover:text-black transition-all group"
    >
        <div className="p-2 bg-rose-500/10 rounded-lg group-hover:bg-black/10">
            <Icon className="w-4 h-4 text-rose-500" />
        </div>
        <span className="font-black text-[10px] uppercase tracking-widest">{label}</span>
    </button>
);

export default UserProfilePage;
